import { Icon } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { IComponent, IconName } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { accordionStylesDefault, IAccordionStyles } from './Accordion.styles';

export interface IAccordionProps extends IComponent<IAccordionStyles>, HTMLAttributes<HTMLDivElement> {
  iconName?: string;
  title?: string;
  subTitle?: string;
  isClose?: boolean;
  headerRenderer?: () => ReactNode | undefined;
  children?: ReactNode;
}

interface IAccordionClassNames {
  root: string;
  content: string;
  header: string;
  headerContent: string;
  icon: string;
  title: string;
  subTitle: string;
  body: string;
}

export const Accordion = forwardRef((props: IAccordionProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    iconName = IconName.ChevronRight,
    title,
    subTitle,
    isClose,
    styles,
    headerRenderer,
    children,
    ...elementProps
  } = props;
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  const [close, setClose] = useState(!!isClose);
  const refRoot = useRef<HTMLDivElement>(null);
  const refContent = useRef<HTMLDivElement>(null);
  const classNames = useClassNames<IAccordionStyles, IAccordionClassNames>(
    'Accordion',
    accordionStylesDefault,
    styles,
    className,
    close && 'is-close'
  );
  useEffect(() => {
    if (refRoot.current && refContent.current) {
      // eslint-disable-next-line react-hooks/immutability
      refRoot.current.style.height = `${refContent.current.offsetHeight}px`;
    }
  }, []);
  return (
    <div {...elementProps} className={classNames.root} ref={refRoot} data-test={props['data-test'] || 'accordion'}>
      <div className={classNames.content} ref={refContent}>
        <div className={classNames.header} onClick={() => setClose(!close)}>
          <div className={classNames.icon}>
            <Icon iconName={iconName} />
          </div>
          {headerRenderer ? (
            headerRenderer()
          ) : (
            <div className={classNames.headerContent} title={`${title} ${subTitle}`}>
              <div className={classNames.title}>{title}</div>
              <div className={classNames.subTitle}>{subTitle}</div>
            </div>
          )}
        </div>
        <div className={classNames.body}>{children}</div>
      </div>
    </div>
  );
});
